import { useMutation, useQueryClient } from '@tanstack/react-query';
import { User } from 'models/User';
import type { MediaId } from 'models/Media';
import { userQuery } from 'networking/mindfeed/queries/user';
import { mediaQuery } from 'networking/mindfeed/queries/media';
import { uploadProfileImage } from 'networking/mindfeed/services/media.service';
import { setProfileImage } from 'networking/mindfeed/services/user.service';
import useAuthenticatedUser from 'networking/mindfeed/hooks/useAuthenticatedUser';
import { notify, NotificationType } from 'xstate/actors/NotificationActor';
import { imageFilename, toProfileJpeg } from 'utils/ImageUtils';

// The new photo: a picked file, the address of a photo to copy (the user's
// Google or Facebook photo, through our image optimiser), or null to remove
// it.
export type ProfileImageSource = Blob | string | null;

type Context = {
  previousUser?: User;
};

// Downloads a photo to upload it again.
const downloadImage = async (url: string): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok)
    throw new Error(`Image download failed: ${response.status}`);
  return response.blob();
};

// Uploads the photo to our servers, then saves it on the user.
const saveProfileImage = async (
  userId: string,
  source: ProfileImageSource,
): Promise<MediaId | null> => {
  if (source === null) {
    await setProfileImage(null);
    return null;
  }

  const original =
    typeof source === 'string' ? await downloadImage(source) : source;
  const image = await toProfileJpeg(original);
  const mediaId = await uploadProfileImage(userId, image, imageFilename(image));
  await setProfileImage(mediaId);
  return mediaId;
};

// Sets or removes the signed-in user's profile photo.
export const useSetProfileImage = () => {
  const queryClient = useQueryClient();
  const { authenticatedUser } = useAuthenticatedUser();
  const userKey = userQuery().queryKey;

  return useMutation<MediaId | null, Error, ProfileImageSource, Context>({
    mutationFn: (source) => {
      if (!authenticatedUser) {
        throw new Error('Not signed in');
      }
      return saveProfileImage(authenticatedUser.id, source);
    },

    // A removal shows straight away. A new photo has no id until it is
    // uploaded, so the modal shows it from the file meanwhile.
    onMutate: async (source) => {
      if (source !== null) {
        return {};
      }
      await queryClient.cancelQueries({ queryKey: userKey });
      const previousUser = queryClient.getQueryData<User>(userKey);
      queryClient.setQueryData<User>(
        userKey,
        (user) =>
          user &&
          new User({
            ...user,
            profileImageId: undefined,
            profileImage: undefined,
          }),
      );
      return { previousUser };
    },

    onError: (_error, _source, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(userKey, context.previousUser);
      }
    },

    // Load the new photo's link first, so avatars go straight from the old
    // photo to the new one instead of flashing the initials. Then reload the
    // user from the server.
    onSuccess: async (mediaId) => {
      if (mediaId) {
        await queryClient.prefetchQuery(mediaQuery(mediaId));
      }
      await queryClient.invalidateQueries({ queryKey: userKey });
      notify(NotificationType.USER_UPDATED);
    },
  });
};
